/**
 * V157.4: Global Request Tracker
 * Prevents duplicate API calls across entire application
 * Enforces cooldown periods for identical queries
 */

interface RequestMetadata {
  query: string;
  timestamp: number;
  inProgress: boolean;
  result?: any;
}

class RequestTracker {
  private requests: Map<string, RequestMetadata> = new Map();
  private recentQueries: Map<string, number> = new Map(); // V157.6-FINAL: Track last 500ms
  private readonly DEFAULT_COOLDOWN = 300000; // V157.6-ULTIMATE: 5 minutes (was 30 seconds)
  
  /**
   * Check if request is allowed or should be blocked
   */
  canExecute(query: string, cooldownMs: number = this.DEFAULT_COOLDOWN): boolean {
    const now = Date.now();

    // V157.6-FINAL: Block if same query executed in last 500ms
    const lastExecution = this.recentQueries.get(query);
    if (lastExecution && (now - lastExecution) < 500) {
      // V159.2.1: Silenced log spam
      // console.log('🚫 V157.6-FINAL: Blocked duplicate within 500ms:', query.substring(0, 50));
      return false;
    }

    const metadata = this.requests.get(query);

    if (!metadata) {
      return true; // First time, allow
    }
    
    const timeSinceRequest = Date.now() - metadata.timestamp;
    
    // Block if still in progress
    if (metadata.inProgress) {
      // V159.2.1: Silenced log spam
      // console.log('🚫 V157.4: Request blocked (already in progress):', {
      //   query: query.substring(0, 80),
      //   timeWaiting: `${timeSinceRequest}ms`
      // });
      return false;
    }
    
    // Block if within cooldown period
    if (timeSinceRequest < cooldownMs) {
      // V159.2.1: Silenced log spam
      // console.log('🚫 V157.4: Request blocked (cooldown active):', {
      //   query: query.substring(0, 80),
      //   timeSinceRequest: `${Math.floor(timeSinceRequest / 1000)}s`,
      //   cooldown: `${cooldownMs / 1000}s`,
      //   remainingCooldown: `${Math.floor((cooldownMs - timeSinceRequest) / 1000)}s`
      // });
      return false;
    }
    
    return true; // Cooldown expired, allow
  }
  
  /**
   * Mark request as started
   */
  startRequest(query: string) {
    const now = Date.now();

    // V157.6-FINAL: Track recent execution
    this.recentQueries.set(query, now);

    // Cleanup old entries (>1 second old)
    for (const [key, timestamp] of this.recentQueries.entries()) {
      if (now - timestamp > 1000) {
        this.recentQueries.delete(key);
      }
    }

    this.requests.set(query, {
      query,
      timestamp: now,
      inProgress: true
    });
    
    // V159.2.1: Silenced log spam
    // console.log('⏱️ V157.4: Request started:', {
    //   query: query.substring(0, 80),
    //   totalTracked: this.requests.size
    // });
  }
  
  /**
   * Mark request as completed and store result
   */
  completeRequest(query: string, result?: any) {
    const metadata = this.requests.get(query);
    if (metadata) {
      metadata.inProgress = false;
      metadata.timestamp = Date.now();
      metadata.result = result;
    }
    
    // V159.2.1: Silenced log spam
    // console.log('✅ V157.4: Request completed:', {
    //   query: query.substring(0, 80),
    //   hasResult: !!result
    // });
  }
  
  /**
   * Get cached result if available and not expired
   */
  getCachedResult<T>(query: string, maxAgeMs: number = this.DEFAULT_COOLDOWN): T | null {
    const metadata = this.requests.get(query);
    
    if (!metadata || !metadata.result) {
      return null;
    }
    
    const age = Date.now() - metadata.timestamp;
    if (age > maxAgeMs) {
      return null; // Expired
    }
    
    // V159.2.1: Silenced log spam
    // console.log('💾 V157.4: Using cached result:', {
    //   query: query.substring(0, 80),
    //   age: `${Math.floor(age / 1000)}s`,
    //   maxAge: `${maxAgeMs / 1000}s`
    // });
    
    return metadata.result as T;
  }
  
  /**
   * Clear all tracked requests (for testing/debugging)
   */
  clear() {
    console.log('🧹 V157.4: Clearing request tracker:', {
      clearedRequests: this.requests.size
    });
    this.requests.clear();
  }
  
  /**
   * Get stats for monitoring
   */
  getStats() {
    const inProgress = Array.from(this.requests.values()).filter(r => r.inProgress).length;
    const completed = this.requests.size - inProgress;
    
    return {
      total: this.requests.size,
      inProgress,
      completed,
      oldestRequest: Math.min(
        ...Array.from(this.requests.values()).map(r => Date.now() - r.timestamp)
      )
    };
  }
}

// Global singleton instance
export const requestTracker = new RequestTracker();

// Export for debugging in console
if (typeof window !== 'undefined') {
  (window as any).__requestTracker = requestTracker;
}
